// web/src/services/prismStore.ts
// FRAME — PRISM on-device persistence layer (localStorage)
//
// Follows the UsageTracker pattern. Single localStorage key for all PRISM data.
// Storage budget: <1MB (behavioral events ~360KB, aesthetics ~375KB, vector ~500B).
// Auto-prunes behavioral events older than 90 days on read.
//
// PRIVACY: Only scalar floats, category labels, boolean flags, and preference weights
// are stored. NO face embeddings, landmarks, bounding boxes, or raw pixel data.

import type {
  PrismStoreData,
  PrismBehavioralEvent,
  PrismAestheticProperties,
  PrismPreferenceVector,
  PrismOwnerBaseline,
  PrismOwnerEnrollment,
} from '@frame/scoring-types';
import { DEFAULT_PRISM_STORE } from '@frame/scoring-types';

const STORAGE_KEY = 'frame_prism';
const PRUNE_INTERVAL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days
const MAX_AESTHETICS_ENTRIES = 500; // Cap to prevent unbounded growth

function read(): PrismStoreData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as PrismStoreData;
      // Migrate missing fields from defaults
      return { ...DEFAULT_PRISM_STORE, ...parsed };
    }
  } catch { /* corrupted — reset */ }
  return { ...DEFAULT_PRISM_STORE };
}

function write(data: PrismStoreData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    // localStorage full — prune aggressively
    console.warn('[PRISM] localStorage write failed, pruning:', e);
    pruneAggressive(data);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch { /* give up */ }
  }
}

function pruneAggressive(data: PrismStoreData): void {
  // Remove oldest 50% of behavioral events
  if (data.behavioralEvents.length > 10) {
    data.behavioralEvents = data.behavioralEvents.slice(Math.floor(data.behavioralEvents.length / 2));
  }
  // Remove oldest aesthetics
  const keys = Object.keys(data.photoAesthetics);
  if (keys.length > 100) {
    const toRemove = keys.slice(0, keys.length - 100);
    for (const k of toRemove) delete data.photoAesthetics[k];
  }
}

export const PrismStore = {
  /** Get the full PRISM store (with auto-prune on read). */
  get(): PrismStoreData {
    const data = read();
    // Auto-prune behavioral events older than 90 days
    const cutoff = Date.now() - PRUNE_INTERVAL_MS;
    const before = data.behavioralEvents.length;
    data.behavioralEvents = data.behavioralEvents.filter(
      e => new Date(e.timestamp).getTime() > cutoff,
    );
    if (data.behavioralEvents.length < before) {
      data.lastPruneDate = new Date().toISOString();
      write(data);
    }
    return data;
  },

  /** Check if PRISM is enabled. */
  isEnabled(): boolean {
    return read().enabled;
  },

  /** Enable or disable PRISM. */
  setEnabled(enabled: boolean): void {
    const data = read();
    data.enabled = enabled;
    write(data);
  },

  /** Check if learning is paused. */
  isPaused(): boolean {
    return read().paused;
  },

  /** Pause or resume preference learning. */
  setPaused(paused: boolean): void {
    const data = read();
    data.paused = paused;
    write(data);
  },

  /** Record a behavioral event. */
  recordEvent(event: PrismBehavioralEvent): void {
    const data = read();
    data.behavioralEvents.push(event);
    write(data);
  },

  /** Store aesthetic properties for a scored photo. */
  storeAesthetics(photoId: string, props: PrismAestheticProperties): void {
    const data = read();
    data.photoAesthetics[photoId] = props;
    // Cap entries to prevent unbounded growth
    const keys = Object.keys(data.photoAesthetics);
    if (keys.length > MAX_AESTHETICS_ENTRIES) {
      const toRemove = keys.slice(0, keys.length - MAX_AESTHETICS_ENTRIES);
      for (const k of toRemove) delete data.photoAesthetics[k];
    }
    write(data);
  },

  /** Batch store aesthetics for a scoring session. */
  storeSessionAesthetics(photos: Array<{ id: string; aesthetics: PrismAestheticProperties }>): void {
    const data = read();
    for (const p of photos) {
      data.photoAesthetics[p.id] = p.aesthetics;
    }
    // Cap entries
    const keys = Object.keys(data.photoAesthetics);
    if (keys.length > MAX_AESTHETICS_ENTRIES) {
      const toRemove = keys.slice(0, keys.length - MAX_AESTHETICS_ENTRIES);
      for (const k of toRemove) delete data.photoAesthetics[k];
    }
    write(data);
  },

  /** Get the preference vector. */
  getPreferenceVector(): PrismPreferenceVector {
    return read().preferenceVector;
  },

  /** Update the preference vector (after learning step). */
  updatePreferenceVector(vector: PrismPreferenceVector): void {
    const data = read();
    data.preferenceVector = vector;
    write(data);
  },

  /** Get the owner baseline. */
  getOwnerBaseline(): PrismOwnerBaseline | null {
    return read().ownerBaseline;
  },

  /** Update the owner baseline. */
  updateOwnerBaseline(baseline: PrismOwnerBaseline): void {
    const data = read();
    data.ownerBaseline = baseline;
    write(data);
  },

  /** Get enrollment state. */
  getEnrollment(): PrismOwnerEnrollment {
    return read().enrollment;
  },

  /** Update enrollment state. */
  updateEnrollment(enrollment: PrismOwnerEnrollment): void {
    const data = read();
    data.enrollment = enrollment;
    write(data);
  },

  /** Get all stored aesthetic properties (for preference learning). */
  getAestheticsMap(): Record<string, PrismAestheticProperties> {
    return read().photoAesthetics;
  },

  /** Get all behavioral events (for preference learning). */
  getEvents(): PrismBehavioralEvent[] {
    return this.get().behavioralEvents; // Uses get() which auto-prunes
  },

  /** Reset ALL PRISM data. Called from Settings > Adaptive Scoring > Reset. */
  reset(): void {
    localStorage.removeItem(STORAGE_KEY);
  },

  /** Export all PRISM data for user download */
  export(): Record<string, unknown> {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { message: 'No PRISM data stored' };
    try { return JSON.parse(raw); } catch { return { message: 'Data corrupted' }; }
  },
};
